/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiBadgeGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiLink,
  EuiPanel,
  EuiStat,
  EuiText,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import moment from "moment";
import { filesize } from "utils/filesize";
import { Tool } from "./ToolBrowser.api";
import { ToolStatus } from "types/constants";
import { ButtonToolInfo } from "./ButtonToolInfo";
import { ButtonToolActions } from "./ButtonToolActions";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { Tag } from "./Tag";
import { ButtonPinTool } from "./ButtonPinTool";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";

/** Lookup map for user-facing tool status names */
const ToolStatusMap: Record<ToolStatus, string> = {
  [ToolStatus.DRAFT]: "Draft",
  [ToolStatus.PUBLISHED]: "Available",
  [ToolStatus.DEPRECATED]: "Deprecated",
};

const getStyles = (euiTheme: ReturnType<typeof useEuiTheme>["euiTheme"]) => ({
  name: css`
    font-size: ${euiTheme.size.l};
  `,
  linkText: css`
    word-break: break-word;
  `,
});

interface ToolCardProps extends Tool {
  onDelete: () => void;
}

export const ToolCard = ({
  id,
  name,
  help_text,
  user,
  created,
  latest_tool_version,
  status,
  tenant,
  pinned,
  visibility,
  docs_link,
  code_link,
  tags,
  onDelete,
}: ToolCardProps) => {
  const updated = latest_tool_version ? latest_tool_version.created : created;
  const description = help_text ?? "";
  const { euiTheme } = useEuiTheme();
  const styles = getStyles(euiTheme);
  const routeMap = useRouteMapContext((s) => s.routeMap);
  const currentTenant = useTenantContext((s) => s.currentTenant);

  return (
    <EuiPanel hasBorder>
      <EuiFlexGroup direction="column">
        <EuiFlexGroup
          alignItems="flexStart"
          justifyContent="spaceBetween"
          responsive={false}
          gutterSize="none"
        >
          <EuiFlexItem
            grow={false}
            css={{
              marginRight: "10px",
              alignSelf: "flex-start",
            }}
          >
            <EuiIcon type="wrench" size="l" />
          </EuiFlexItem>

          <EuiFlexItem grow>
            <EuiFlexGroup direction="column" gutterSize="none">
              <EuiFlexItem>
                <EuiText css={styles.linkText}>
                  <EuiLink
                    css={styles.name}
                    to={
                      routeMap["TOOL"].dynamicPath({
                        toolId: id,
                        tenantKey: currentTenant.key,
                      }).path
                    }
                  >
                    {name}
                  </EuiLink>
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="s" css={{ lineHeight: "15px" }} color="subdued">
                  updated {moment(updated).fromNow()}
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiFlexGroup gutterSize="none" alignItems="center">
              <ButtonPinTool toolId={id} pinned={pinned} />
              <ButtonToolActions
                id={id}
                name={name}
                helpText={help_text ?? ""}
                visibility={visibility}
              />
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>

        {description !== "" && (
          <EuiFlexItem>
            <EuiText>{description}</EuiText>
          </EuiFlexItem>
        )}

        {tags.length > 0 && (
          <EuiFlexItem grow={false}>
            <EuiBadgeGroup gutterSize="s">
              {tags.map((tag) => (
                <Tag key={tag} tag={tag} />
              ))}
            </EuiBadgeGroup>
          </EuiFlexItem>
        )}

        <EuiFlexGroup responsive={false} alignItems="center" gutterSize="m">
          <EuiFlexItem>
            <EuiFlexGroup alignItems="flexStart" responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiStat
                  titleSize="xs"
                  title={latest_tool_version?.version ?? "0.0.0"}
                  description="Latest Version"
                  reverse
                />
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <EuiStat
                  titleSize="xs"
                  title={filesize("0")}
                  description="Storage"
                  reverse
                />
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <EuiStat
                  titleSize="xs"
                  title={ToolStatusMap[status]}
                  description="Status"
                  reverse
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            {tenant ? (
              <EuiLink
                to={
                  routeMap["ORGANIZATION"].dynamicPath({
                    tenantKey: tenant.key,
                  }).path
                }
              >
                {tenant.name}
              </EuiLink>
            ) : (
              <EuiText color="subdued">Inscopix</EuiText>
            )}
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <ButtonToolInfo
              dateCreated={created}
              dateUpdated={updated}
              user={user}
              docsLink={docs_link}
              codeLink={code_link}
              anchorPosition="leftDown"
              repositionOnScroll
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexGroup>
    </EuiPanel>
  );
};
